/**
 * Stripe API Service
 * Integração com o backend Node.js para gerenciamento de assinaturas Stripe
 */

const API_URL = import.meta.env.VITE_SERVICE_URL_HOSTLY_API;
const API_KEY = import.meta.env.VITE_API_SECRET_KEY;

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

class StripeApiService {
  private headers = {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  };

  // ============================================
  // CUSTOMERS & SUBSCRIPTIONS
  // ============================================

  async getSubscriptionStatus(userId: string) {
    const response = await fetch(`${API_URL}/status/${userId}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async createCustomer(data: {
    email: string;
    name?: string;
    metadata?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/customers`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async getCustomer(customerId: string) {
    const response = await fetch(`${API_URL}/customers/${customerId}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async listCustomerSubscriptions(customerId: string) {
    const response = await fetch(`${API_URL}/customers/${customerId}/subscriptions`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async createCheckoutSession(data: {
    customerId?: string;
    priceId?: string;
    planKey?: string;
    successUrl: string;
    cancelUrl: string;
    metadata?: Record<string, string>;
    email?: string;
    promotionCode?: string;
  }) {
    const response = await fetch(`${API_URL}/checkout-sessions`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async createPortalSession(data: {
    customer: string;
    return_url: string;
  }) {
    const response = await fetch(`${API_URL}/portal-sessions`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({
        customerId: data.customer,
        returnUrl: data.return_url,
      }),
    });
    return this.handleResponse(response);
  }

  async getSubscription(subscriptionId: string) {
    const response = await fetch(`${API_URL}/subscriptions/${subscriptionId}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async cancelSubscription(subscriptionId: string) {
    const response = await fetch(`${API_URL}/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  // ============================================
  // PRODUCTS & PRICES
  // ============================================

  async createProduct(data: {
    name: string;
    description?: string;
    active?: boolean;
    metadata?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/api/products/products`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async listProducts(params?: { active?: boolean; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.active !== undefined) queryParams.append('active', String(params.active));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await fetch(`${API_URL}/products?${queryParams}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async getProduct(productId: string) {
    const response = await fetch(`${API_URL}/api/products/products/${productId}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async updateProduct(productId: string, data: {
    name?: string;
    description?: string;
    active?: boolean;
    metadata?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/api/products/products/${productId}`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async deleteProduct(productId: string) {
    const response = await fetch(`${API_URL}/api/products/products/${productId}`, {
      method: 'DELETE',
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async createPrice(data: {
    product: string;
    unitAmount: number;
    currency: string;
    nickname?: string;
    recurring?: {
      interval: 'day' | 'week' | 'month' | 'year';
      interval_count?: number;
    };
    metadata?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/api/products/prices`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async listPrices(params?: { product?: string; active?: boolean; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.product) queryParams.append('product', params.product);
    if (params?.active !== undefined) queryParams.append('active', String(params.active));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await fetch(`${API_URL}/api/products/prices?${queryParams}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async getPrice(priceId: string) {
    const response = await fetch(`${API_URL}/api/products/prices/${priceId}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async updatePrice(priceId: string, data: {
    metadata?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/api/products/prices/${priceId}`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async deactivatePrice(priceId: string) {
    const response = await fetch(`${API_URL}/api/products/prices/${priceId}`, {
      method: 'DELETE',
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  // ============================================
  // COUPONS & PROMOTION CODES
  // ============================================

  async createCoupon(data: {
    percentOff?: number;
    amountOff?: number;
    currency?: string;
    duration: 'forever' | 'once' | 'repeating';
    durationInMonths?: number;
    maxRedemptions?: number;
    name?: string;
    metadata?: Record<string, string>;
    appliesToProducts?: string[];
  }) {
    const response = await fetch(`${API_URL}/coupons`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async listCoupons(params?: { limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await fetch(`${API_URL}/coupons?${queryParams}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async getCoupon(couponId: string) {
    const response = await fetch(`${API_URL}/coupons/${couponId}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async deleteCoupon(couponId: string) {
    const response = await fetch(`${API_URL}/coupons/${couponId}`, {
      method: 'DELETE',
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async createPromotionCode(data: {
    coupon: string;
    code: string;
    active?: boolean;
    max_redemptions?: number;
    metadata?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/promotion-codes`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async listPromotionCodes(params?: { active?: boolean; limit?: number; code?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.active !== undefined) queryParams.append('active', String(params.active));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.code) queryParams.append('code', params.code);

    const response = await fetch(`${API_URL}/promotion-codes?${queryParams}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async findActivePromotionCodeByName(code: string) {
    const result = await this.listPromotionCodes({ code, active: true, limit: 1 });
    if (!result.success) return null;
    const list = (result.data as any)?.promotionCodes?.data || [];
    return list[0] || null;
  }

  async getPromotionCode(promoCodeId: string) {
    const response = await fetch(`${API_URL}/promotion-codes/${promoCodeId}`, {
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  async deactivatePromotionCode(codeId: string) {
    const response = await fetch(`${API_URL}/promotion-codes/${codeId}`, {
      method: 'DELETE',
      headers: this.headers,
    });
    return this.handleResponse(response);
  }

  // ============================================
  // NOTIFICATIONS
  // ============================================

  async sendNotification(data: {
    token: string;
    title: string;
    body: string;
    data?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/api/notifications/send`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async sendMulticastNotification(data: {
    tokens: string[];
    title: string;
    body: string;
    data?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/api/notifications/send-multicast`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async sendTopicNotification(data: {
    topic: string;
    title: string;
    body: string;
    data?: Record<string, string>;
  }) {
    const response = await fetch(`${API_URL}/api/notifications/send-topic`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async subscribeToTopic(data: {
    tokens: string[];
    topic: string;
  }) {
    const response = await fetch(`${API_URL}/api/notifications/subscribe-topic`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async unsubscribeFromTopic(data: {
    tokens: string[];
    topic: string;
  }) {
    const response = await fetch(`${API_URL}/api/notifications/unsubscribe-topic`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  // ============================================
  // HEALTH CHECK
  // ============================================

  async healthCheck() {
    const response = await fetch(`${API_URL}/health`);
    return this.handleResponse(response);
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  private async handleResponse<T = any>(response: Response): Promise<ApiResponse<T>> {
    try {
      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.message || 'Erro desconhecido',
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao processar resposta',
      };
    }
  }
}

export const stripeApi = new StripeApiService();
